import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { NodeId } from 'web';
import { log } from '@/logger/logger.ts';
import { PreviewData } from '@/types/node';
import { I18N_KEYS } from '@/language/keys';
import { useUserWorkflowRole, useWorkflowStore } from '@/state/workflow.state';
import { useNodeInput, usePreviewView } from '@/components/Recipe/FlowContext';
import { DynamicNode2 } from '../DynamicNode/DynamicNode2';
import { NodeViewer } from '../Shared/FileViewer';
import type { MenuAction } from '@/components/Menu/Actions';

const logger = log.getLogger('PreviewNode');

function PreviewNodeV2({ id, data }: { id: NodeId; data: PreviewData }) {
  const { t } = useTranslation();
  const role = useUserWorkflowRole();
  const setRecipePoster = useWorkflowStore((s) => s.setRecipePoster);
  const editable = data.isLocked !== true && role === 'editor';
  const previewView = usePreviewView(id);
  const input = useNodeInput(id, 'file');

  const handleSetPoster = useCallback(async () => {
    try {
      const url = previewView.evaluateImageInputUrl();
      if (url) {
        await setRecipePoster(url);
      }
    } catch (error) {
      logger.error('error saving as cover from preview node', error);
    }
  }, [previewView, setRecipePoster]);

  const contextMenuItems = useMemo<MenuAction[]>(
    () => [
      {
        name: t(I18N_KEYS.RECIPE_MAIN.NODES.PREVIEW_NODE.SET_AS_COVER),
        action: () => void handleSetPoster(),
      },
    ],
    [handleSetPoster, t],
  );
  const viewerContextMenuItems = editable && input?.type === 'image' ? contextMenuItems : undefined;

  return (
    <DynamicNode2 id={id} data={data} className="preview">
      <NodeViewer id={id} contextMenuItems={viewerContextMenuItems} />
    </DynamicNode2>
  );
}

export default PreviewNodeV2;
